import { createAuthClient } from "@neondatabase/auth";

export const RICE_LIGHTNING_BASE_URL = "https://ricelightn.ing";

export const RICE_LIGHTNING_AUTH_BASE_URL =
  typeof window !== "undefined"
    ? `${window.location.origin}/api/auth`
    : `${RICE_LIGHTNING_BASE_URL}/api/auth`;

export const authClient = createAuthClient(RICE_LIGHTNING_AUTH_BASE_URL);

export type RiceLightningUser = {
  id: string;
  email: string;
  name?: string | null;
  emailVerified?: boolean;
  role?: string | null;
  createdAt?: string | Date | null;
};

export type RiceLightningAuthState = {
  authenticated: boolean;
  authorized: boolean;
  user: RiceLightningUser | null;
};

type AuthApiResult<T> = {
  data: T | null;
  error: null | { message: string; status?: number };
};

async function postAuth<T>(path: string, body: Record<string, unknown>): Promise<AuthApiResult<T>> {
  const response = await fetch(`${RICE_LIGHTNING_AUTH_BASE_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => null) as T | { message?: string; error?: string } | null;
  if (!response.ok) {
    const message =
      payload && typeof payload === "object"
        ? ("message" in payload && typeof payload.message === "string" && payload.message) ||
          ("error" in payload && typeof payload.error === "string" && payload.error) ||
          `Authentication request failed (${response.status}).`
        : `Authentication request failed (${response.status}).`;
    return { data: null, error: { message, status: response.status } };
  }

  return { data: payload as T, error: null };
}

export async function getRiceLightningAuthState(): Promise<RiceLightningAuthState> {
  const response = await fetch("/api/auth-state", {
    method: "GET",
    credentials: "include",
    headers: { accept: "application/json" },
  });

  if (!response.ok) return { authenticated: false, authorized: false, user: null };
  return response.json() as Promise<RiceLightningAuthState>;
}

export async function signInRiceLightningWithEmail(
  email: string,
  password: string,
  returnTo = RICE_LIGHTNING_BASE_URL,
) {
  return postAuth<{ user?: RiceLightningUser; token?: string; redirect?: boolean; url?: string }>("/sign-in/email", {
    email,
    password,
    rememberMe: true,
    callbackURL: returnTo,
  });
}

export async function changeRiceLightningPassword(currentPassword: string, newPassword: string) {
  return authClient.changePassword({
    currentPassword,
    newPassword,
    revokeOtherSessions: true,
  });
}

export async function signOutRiceLightning() {
  return authClient.signOut();
}

export async function getRiceLightningJwt() {
  const result = await authClient.token();
  if (result.error) throw result.error;
  return result.data?.token ?? null;
}
